import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { documenti as documentiApi, cantieri } from '../../utils/api';
import DocumentiTabPDF from './DocumentiTabPDF';
import UploadDocumentoModal from '../documenti/UploadDocumentoModal';
import { Folder, FileText, Image as ImageIcon, Film } from 'lucide-react';
import useArchivioBlocchi from '../../pages/documenti/useArchivioBlocchi';
import CaricaAltri from '../documenti/CaricaAltri';

const TIPI_DOC = [
  { value: 'preventivo',     label: 'Preventivo' },
  { value: 'contratto',      label: 'Contratto' },
  { value: 'fattura',        label: 'Fattura' },
  { value: 'foto',           label: 'Foto' },
  { value: 'ddt',            label: 'DDT' },
  { value: 'bolla',          label: 'Bolla' },
  { value: 'certificato',    label: 'Certificato' },
  { value: 'collaudo',       label: 'Collaudo' },
  { value: 'planimetria',    label: 'Planimetria' },
  { value: 'sal',            label: 'SAL' },
  { value: 'computo',        label: 'Computo metrico' },
  { value: 'corrispondenza', label: 'Corrispondenza' },
  { value: 'pdf_generico',   label: 'PDF generico' },
  { value: 'altro',          label: 'Altro' },
];

const slugify = s => s.toLowerCase().replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e').replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o').replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

// Icona Lucide per tipo documento: foto -> Image, video -> Film, resto -> FileText.
const DOC_ICON = { foto: ImageIcon, video: Film };

export default function DocumentiTab({ cantiereId, dashData }) {
  const navigate = useNavigate();
  // FE-084 — a blocchi da 50 con «Carica altri», come l'archivio.
  const elenco = useArchivioBlocchi({ cantiere: cantiereId });
  const docs = elenco.docs;
  const loading = elenco.loading;
  const fetch = elenco.ricarica;
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Mini-panel inline: file selezionato in attesa di conferma tipo
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingTipo, setPendingTipo] = useState('altro');
  const [pendingNote, setPendingNote] = useState('');
  const fileRef = useRef(null);


  // Drag & drop / input nascosto: mostra mini-panel invece di caricare subito.
  // L'utente sceglie il tipo prima dell'upload (signal Django ha bisogno
  // del tipo corretto per assegnare la sottocartella giusta).
  const handleFileSelected = (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setPendingFile(file);
    setPendingTipo(guessType(file.name));
    setPendingNote('');
  };

  const cancelPending = () => {
    setPendingFile(null);
    setPendingTipo('altro');
    setPendingNote('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const confirmPendingUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('cantiere', cantiereId);
      fd.append('tipo', pendingTipo);
      if (pendingNote) fd.append('note', pendingNote);
      await documentiApi.upload(fd);
      cancelPending();
      fetch();
    } catch {}
    finally { setUploading(false); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try { await documentiApi.delete(docId); fetch(); } catch {}
  };

  const handleImportAI = async (docId) => {
    try {
      await cantieri.importaContratto(cantiereId, '');
      alert('Analisi AI avviata. Le fasi verranno estratte dal documento.');
    } catch {}
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFileSelected(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  if (loading) return <div style={{ padding: 20, color: 'var(--ds-muted)' }}>Caricamento documenti...</div>;
  if (elenco.error) return (
    <div style={{ padding: 20, color: 'var(--ds-danger)', fontSize: 13 }}>
      {elenco.error}{' '}
      <button onClick={elenco.ricarica} style={{ marginLeft: 8, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>Riprova</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500 }}>Documenti</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Il PDF elenca TUTTI i documenti: se non sono ancora tutti caricati,
              il primo clic li carica, poi compare il collegamento (FE-084). */}
          {docs.length > 0 && elenco.ciSonoAltri && (
            <button style={actionBtn} disabled={elenco.loadingAltri} onClick={elenco.caricaTutti}
              title="Carica tutti i documenti del cantiere per il PDF">
              {elenco.loadingAltri ? '...' : '📄 Prepara PDF'}
            </button>
          )}
          {docs.length > 0 && !elenco.ciSonoAltri && (
            <PDFDownloadLink
              document={<DocumentiTabPDF documenti={docs} cantiere={dashData?.cantiere} azienda={dashData?.azienda} />}
              fileName={'documenti-' + slugify(dashData?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
            >
              {({ loading }) => <button style={actionBtn} disabled={loading}>{loading ? '...' : '📄 PDF'}</button>}
            </PDFDownloadLink>
          )}
          <button onClick={() => setShowModal(true)} style={btnPrimary} disabled={uploading}>
            {uploading ? 'Caricamento...' : '+ Carica documento'}
          </button>
        </div>
        <input ref={fileRef} type="file" hidden onChange={e => handleFileSelected(e.target.files)} />
      </div>

      {/* Mini-panel inline: scelta tipo prima dell'upload (drag&drop o click drop zone) */}
      {pendingFile && (
        <div style={{
          padding: 16, marginBottom: 16, borderRadius: 'var(--radius-card)',
          border: '1px solid var(--brand)', background: 'rgba(46,160,132,0.12)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 12 }}>
            Conferma tipo documento
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={panelLbl}>Nome file</label>
              <div style={{ fontSize: 13, color: 'var(--ds-muted)', wordBreak: 'break-all' }}>
                {pendingFile.name} ({(pendingFile.size / 1024).toFixed(0)} KB)
              </div>
            </div>
            <div>
              <label style={panelLbl}>Tipo documento</label>
              <select
                value={pendingTipo}
                onChange={e => setPendingTipo(e.target.value)}
                style={panelInput}
              >
                {TIPI_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={panelLbl}>Note (opzionale)</label>
              <textarea
                value={pendingNote}
                onChange={e => setPendingNote(e.target.value)}
                rows={2}
                placeholder="Descrizione breve..."
                style={{ ...panelInput, resize: 'vertical', minHeight: 44 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={cancelPending} style={btnSecondary} disabled={uploading}>
                Annulla
              </button>
              <button onClick={confirmPendingUpload} style={btnPrimary} disabled={uploading}>
                {uploading ? 'Caricamento...' : 'Carica'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal upload completo (bottone "+ Carica documento") */}
      {showModal && (
        <UploadDocumentoModal
          defaultCantiere={cantiereId}
          onClose={() => setShowModal(false)}
          onSuccess={fetch}
        />
      )}

      {/* Drop zone */}
      <div
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        onClick={() => fileRef.current?.click()}
        style={{
          padding: '28px 20px', marginBottom: 20, borderRadius: 'var(--radius-card)',
          border: '2px dashed ' + (dragOver ? 'var(--brand)' : 'var(--ds-border)'),
          background: dragOver ? 'rgba(46,160,132,0.12)' : 'var(--ds-bg)',
          textAlign: 'center', cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 6 }}>📎</div>
        <div style={{ fontSize: 13, color: 'var(--ds-muted)' }}>
          Trascina qui un file o clicca per caricare
        </div>
        <div style={{ fontSize: 12, color: 'var(--brand)', marginTop: 4 }}>
          PDF, DOCX, JPG, PNG fino a 20MB
        </div>
      </div>

      {/* Lista documenti */}
      {docs.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>
          Nessun documento caricato.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {docs.map(d => {
            const isPdf = (d.nome_file || '').toLowerCase().endsWith('.pdf');
            const isContract = d.tipo === 'contratto';
            const DIcon = DOC_ICON[d.tipo] || FileText;
            return (
              <div key={d.id} style={{
                padding: '14px 16px', borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-border)', background: 'var(--chip)',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 'var(--radius-card)', flexShrink: 0,
                  background: 'var(--chip)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <DIcon size={20} strokeWidth={1.8} color="var(--ds-muted)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.nome_file}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
                    {d.tipo && (d.tipo.charAt(0).toUpperCase() + d.tipo.slice(1))}
                    {d.data_upload && (' · ' + fmtDate(d.data_upload))}
                    {d.dimensione_bytes && (' · ' + fmtSize(d.dimensione_bytes))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  {isContract && isPdf && (
                    <button onClick={(e) => { e.stopPropagation(); handleImportAI(d.id); }} style={actionBtn}>
                      🤖 Analizza AI
                    </button>
                  )}
                  {d.file && isPdf && (
                    <a href={d.file} target="_blank" rel="noopener noreferrer" style={{ ...actionBtn, textDecoration: 'none' }}>
                      👁 Anteprima
                    </a>
                  )}
                  {d.file && (
                    <a href={d.file} download style={{ ...actionBtn, textDecoration: 'none' }}>
                      ⬇ Scarica
                    </a>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); navigate('/documenti'); }} style={actionBtn} title="Apri in Archivio">
                    <Folder size={14} strokeWidth={1.8} color="var(--zone-amber)" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }} title="Elimina" style={{
                    border: 'none', background: 'none', cursor: 'pointer',
                    color: 'var(--ds-danger)', padding: '4px', display: 'flex',
                  }}><svg style={{ width: 14, height: 14 }} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg></button>
                </div>
              </div>
            );
          })}
          <CaricaAltri elenco={elenco} />
        </div>
      )}
    </div>
  );
}

function guessType(filename) {
  const ext = (filename || '').toLowerCase().split('.').pop();
  const map = {
    pdf:  'pdf_generico',
    jpg:  'foto', jpeg: 'foto', png: 'foto', gif: 'foto', webp: 'foto',
    docx: 'altro', doc: 'altro',
    xlsx: 'computo', xls: 'computo',
    zip:  'altro', rar: 'altro',
    dwg:  'planimetria', dxf: 'planimetria',
  };
  return map[ext] || 'altro';
}

const btnPrimary = { padding: '10px 20px', borderRadius: 6, border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnSecondary = { padding: '10px 20px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const actionBtn = { padding: '4px 10px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', fontSize: 11, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 };
const panelLbl = { display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' };
const panelInput = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ds-text)' };
